import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Row, Col, Container, Button, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiPackage, FiTruck, FiUser, FiMapPin, FiCalendar } from 'react-icons/fi';
import PageFade from '../../components/animation/PageFade';
import { StaggerFade } from '../../components/animation/StaggerFade';
import { useGetMetadataQuery } from '../../services/api';
import type { ApiOrder } from '../../types';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order as ApiOrder;
  const { data: metadata, isLoading: isMetadataLoading } = useGetMetadataQuery();

  if (!order) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Order Not Found</Alert.Heading>
          <p>We couldn't find the order details in the navigation state. Please return to the orders list and try again.</p>
          <Button variant="outline-primary" onClick={() => navigate('/orders')}>
            <FiArrowLeft className="me-2" /> Back to Orders
          </Button>
        </Alert>
      </Container>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'warning',
      processing: 'primary',
      shipped: 'info',
      delivered: 'success',
      cancelled: 'danger'
    };
    return colors[status] || 'secondary';
  };

  const getColorName = (colorId: string) => {
    return metadata?.data?.colors?.find(c => c._id === colorId)?.name || colorId;
  };

  const getSizeName = (sizeId: string) => {
    return metadata?.data?.sizes?.find(s => s._id === sizeId)?.name || sizeId;
  };

  return (
    <PageFade>
      <Container fluid className="py-4">
        <div className="d-flex align-items-center mb-4">
          <Button 
            variant="link" 
            className="text-decoration-none p-0 me-3" 
            onClick={() => navigate('/orders')}
          >
            <FiArrowLeft size={24} />
          </Button>
          <div>
            <h3 className="mb-0">Order Details</h3>
            <small className="text-muted">ID: {order._id}</small>
          </div>
          <Badge bg={statusColor(order.status)} className="ms-auto fs-6 px-3 py-2">
            {order.status.toUpperCase()}
          </Badge>
        </div>

        <Row className="g-4">
          {/* Main Content: Order Items */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white py-3 border-bottom-0">
                <h5 className="mb-0"><FiPackage className="me-2" /> Order Items</h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4 py-3 border-0">Product</th>
                      <th className="py-3 border-0">Variant Details</th>
                      <th className="py-3 border-0 text-center">Unit Price</th>
                      <th className="py-3 border-0 text-center text-nowrap">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <StaggerFade>
                      {order.items.map((item, idx) => (
                        <tr key={item.productId + idx}>
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <img 
                                src={item.image} 
                                alt={item.name} 
                                className="rounded shadow-sm"
                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                              />
                              <div className="ms-3">
                                <div className="fw-bold">{item.name}</div>
                                <small className="text-muted">ID: {item.productId.slice(-8)}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <ul className="list-unstyled mb-0">
                              {item.quantity.map((q, qIdx) => (
                                <li key={qIdx} className="mb-1 small">
                                  <Badge bg="light" text="dark" className="border me-2">
                                    {q.quantity}x
                                  </Badge>
                                  <span className="text-muted">Color:</span> {isMetadataLoading ? '...' : getColorName(q.color)}
                                  <span className="mx-2">|</span>
                                  <span className="text-muted">Size:</span> {isMetadataLoading ? '...' : getSizeName(q.size)}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="text-center">EGP {item.price.toLocaleString()}</td>
                          <td className="text-center fw-bold">
                            EGP {(item.price * item.quantity.reduce((acc, q) => acc + q.quantity, 0)).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </StaggerFade>
                  </tbody>
                </Table>
              </Card.Body>
              <Card.Footer className="bg-white border-top-0 py-4 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted h5 mb-0">Total Amount</span>
                  <span className="h3 mb-0 text-primary">EGP {order.totalAmount.toLocaleString()}</span>
                </div>
              </Card.Footer>
            </Card>
          </Col>

          {/* Sidebar: Customer & Shipping */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white py-3 border-bottom-0">
                <h5 className="mb-0"><FiUser className="me-2" /> Customer Information</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="text-muted small mb-1">Full Name</div>
                  <div className="fw-bold">{order.userId?.name || 'Guest'}</div>
                </div>
                <div className="mb-3">
                  <div className="text-muted small mb-1">Email Address</div>
                  <div className="fw-bold">{order.guestEmail || order.userId?.email || 'N/A'}</div>
                </div>
                <div className="mb-0">
                  <div className="text-muted small mb-1">Phone Number</div>
                  <div className="fw-bold">{order.guestPhone || 'N/A'}</div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white py-3 border-bottom-0">
                <h5 className="mb-0"><FiMapPin className="me-2" /> Shipping Address</h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-2 fw-bold">{order.shippingAddress.street}</p>
                <p className="mb-1">{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                <p className="mb-1">{order.shippingAddress.zipCode}, {order.shippingAddress.country}</p>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white py-3 border-bottom-0">
                <h5 className="mb-0"><FiCalendar className="me-2" /> Order Info</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="text-muted small mb-1">Placed On</div>
                  <div className="fw-bold">{formatDate(order.createdAt)}</div>
                </div>
                <div className="mb-3">
                  <div className="text-muted small mb-1">Last Updated</div>
                  <div className="fw-bold">{formatDate(order.updatedAt)}</div>
                </div>
                <div className="mb-0">
                  <div className="text-muted small mb-1">Payment Status</div>
                  <Badge bg={order.paymentStatus === 'paid' ? 'success' : 'warning'} className="px-3">
                    {order.paymentStatus.toUpperCase()}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageFade>
  );
}

